import {
	AddOn,
	BillingCycle,
	BillingInterval,
	Currency,
	Price,
} from "../../../index";

enum CartItemAction {
	ADD = "add",
	REMOVE = "remove",
	RESUBSCRIBE = "resubscribe",
	MODIFY_QUANTITY = "modify_quantity",
	CHANGE_BILLING_INTERVAL = "change_billing_interval",
}

// eslint-disable-next-line no-redeclare
namespace CartItemAction {
	export const prefixes = {
		[CartItemAction.ADD]: "+",
		[CartItemAction.REMOVE]: "-",
		[CartItemAction.RESUBSCRIBE]: "+",
		[CartItemAction.MODIFY_QUANTITY]: "",
		[CartItemAction.CHANGE_BILLING_INTERVAL]: "",
	} as any;

	export const colors = {
		[CartItemAction.ADD]: "green",
		[CartItemAction.REMOVE]: "magenta",
		[CartItemAction.RESUBSCRIBE]: "yellow",
		[CartItemAction.MODIFY_QUANTITY]: "blue",
		[CartItemAction.CHANGE_BILLING_INTERVAL]: "blue",
	} as any;

	export const displayNamesInCart = {
		[CartItemAction.ADD]: (name: string) => `Add ${name} to your`,
		[CartItemAction.REMOVE]: (name: string) => `Remove ${name} from your`,
		[CartItemAction.RESUBSCRIBE]: (name: string) => `Resubscribe to ${name} in your`,
		[CartItemAction.MODIFY_QUANTITY]: (name: string) => `Update ${name} in your`,
		[CartItemAction.CHANGE_BILLING_INTERVAL]: (name: string) => `Change billing interval of ${name} in your`,
	} as any;

	export const displayNames = {
		[CartItemAction.ADD]: "Add",
		[CartItemAction.REMOVE]: "Remove",
		[CartItemAction.RESUBSCRIBE]: "Resubscribe",
		[CartItemAction.MODIFY_QUANTITY]: "Update",
		[CartItemAction.CHANGE_BILLING_INTERVAL]: "Update",
	} as any;

	export const prefix = (action: CartItemAction) => {
		return prefixes[action];
	};

	export const color = (action: CartItemAction) => {
		return colors[action];
	};

	export const displayNameInCart = (action: CartItemAction, name: string) => {
		return displayNamesInCart[action](name);
	};

	export const displayName = (action: CartItemAction) => {
		return displayNames[action];
	};
}

interface CartItemProps {
	internalAddOnId: string;
	internalPlanId: string;
	externalPlanId: string;
	userAddOnId?: string;
	action: CartItemAction;
	price?: Price;
	addOn?: AddOn;
	quantity: number;
}

class CartItem {
	constructor(public _: CartItemProps) {}

	static fromJson(json: any) {
		return new CartItem({
			internalAddOnId: json.internal_add_on_id,
			internalPlanId: json.internal_plan_id,
			externalPlanId: json.external_plan_id,
			userAddOnId: json.user_add_on_id,
			action: json.action || CartItemAction.ADD,
			price: json.billing_price
				? Price.fromJson(json.billing_price)
				: undefined,
			addOn: json.add_on ? AddOn.fromJson(json.add_on) : undefined,
			quantity: json.quantity || 1,
		});
	}

	toJson(withAddOn: boolean = true) {
		const res = {
			internal_add_on_id: this._.internalAddOnId,
			internal_plan_id: this._.internalPlanId,
			external_plan_id: this._.externalPlanId,
			user_add_on_id: this._.userAddOnId,
			action: this._.action,
			billing_price: this._.price?.toJson(),
			quantity: this._.quantity,
		} as any;
		if (this._.addOn && withAddOn) res.add_on = this._.addOn.toJson();
		return res;
	}

	withQuantity(quantity: number) {
		this._.quantity = quantity;
		return this;
	}

	withAddOn(addOn: AddOn) {
		this._.addOn = addOn;
		return this;
	}

	withBillingInterval(interval: BillingInterval) {
		const billingCycle = new BillingCycle({
			interval: interval,
			frequency: 1,
		});
		const plan = this._.addOn?.planByBillingCycle(billingCycle);
		this._.price = plan
			?.priceByCurrency(this._.price?.currency || Currency.defaultValue);
		this._.internalPlanId = plan?._.internalId || this._.internalPlanId;
		this._.externalPlanId = plan?._.externalId || this._.externalPlanId;
		return this;
	}

	getPrice() {
		if (this._.action === CartItemAction.REMOVE) {
			return this._.price?.withAmount(-this._.price?.amount * this._.quantity);
		} else {
			return this._.price?.withAmount(this._.price?.amount * this._.quantity);
		}
	}

	display(showFree: boolean, showPrefix: boolean) {
		const price = this.getPrice();
		return price?.display(showFree, showPrefix);
	}

	equals(
		other: CartItem,
		ignoreQuantity: boolean = false,
		ignoreAction: boolean = false,
		ignorePlan: boolean = false
	) {
		return (
			this._.internalAddOnId === other._.internalAddOnId &&
			(ignoreQuantity || this._.quantity === other._.quantity) &&
			(ignoreAction || this._.action === other._.action) &&
			(ignorePlan || this._.internalPlanId === other._.internalPlanId)
		);
	}

	copyWith(props: Partial<CartItemProps>) {
		return new CartItem({ ...this._, ...props });
	}
}

export { CartItem, CartItemAction };
